// Anrufbericht (Agent 7A, R-12): vier Karten wie in Close — Anrufe mit Verlauf, nach Dauer, nach Ergebnis, eindeutig angerufene Leads.
import { useMemo, useState } from "react";
import { berichteApi } from "../api/berichte";
import { Laden } from "../components/Bausteine";
import { BerichtSeite } from "./BerichtSeite";
import { useGeladen } from "./daten";
import { Balkenliste, Liniendiagramm } from "./Diagramme";
import { dauerText, ganzText, veraenderungText } from "./format";
import type { Konfig } from "./konfig";
import { useBerichtSteuerung } from "./steuerung";
import { useBerichtFilter } from "./zustand";

type Richtung = "alle" | "ausgehend" | "eingehend";
const RICHTUNGEN: [Richtung, string][] = [["alle", "Alle"], ["ausgehend", "Ausgehend"], ["eingehend", "Eingehend"]];

function Delta({ prozent }: { prozent: number | null | undefined }) {
  if (prozent === null || prozent === undefined) return null;
  const r = prozent > 0 ? "hoch" : prozent < 0 ? "runter" : "gleich";
  return <span className={`kachel__delta kachel__delta--${r}`}>{r === "hoch" ? "▲ " : r === "runter" ? "▼ " : ""}{veraenderungText(prozent)}</span>;
}

export function Anrufe() {
  const filter = useBerichtFilter("diese_woche");
  const [richtung, setRichtung] = useState<Richtung>("alle");
  const zusatz = useMemo<Konfig>(() => ({ richtung }), [richtung]);
  const steuerung = useBerichtSteuerung({
    art: "anrufe", filter, zusatz,
    onAnwenden: (k) => setRichtung(k.richtung === "ausgehend" || k.richtung === "eingehend" ? k.richtung : "alle"),
  });
  const { eingabe, eingabeSchluessel } = filter;
  const abfrage = useGeladen(() => berichteApi.anrufe({ ...eingabe, richtung }), `${eingabeSchluessel}|${richtung}`, steuerung.geladen);
  const a = abfrage.daten;
  const titel = richtung === "ausgehend" ? "Ausgehende Anrufe" : richtung === "eingehend" ? "Eingehende Anrufe" : "Anrufe";
  return (
    <BerichtSeite
      art="anrufe"
      filter={filter}
      steuerung={steuerung}
      fehler={abfrage.fehler}
      onNochmal={abfrage.neuLaden}
      csv={[{ titel: "Anrufbericht als CSV laden", art: "anrufe", parameter: { ...eingabe, richtung } }]}
      weitereFilter={
        <div className="segment" role="group" aria-label="Richtung der Anrufe">
          {RICHTUNGEN.map(([wert, label]) => (
            <button key={wert} type="button" className={`segment__knopf${richtung === wert ? " segment__knopf--an" : ""}`} onClick={() => setRichtung(wert)}>
              {label}
            </button>
          ))}
        </div>
      }
    >
      {!a ? (
        <Laden text="Anrufe werden gezählt …" />
      ) : (
        <div className="diagrammraster" aria-busy={abfrage.laedt}>
          <section className="bkarte">
            <header className="bkarte__kopf">
              <h2 className="bkarte__titel">{titel}</h2>
              <div className="bkarte__zahl">
                {ganzText(a.gesamt)} <Delta prozent={a.veraenderung_prozent} />
              </div>
            </header>
            {a.vergleich_gesamt !== null && a.vergleich_gesamt !== undefined ? <p className="leise bkarte__unter">Vorher: {ganzText(a.vergleich_gesamt)}</p> : null}
            <Liniendiagramm punkte={a.reihe.map((p) => ({ zeit: p.zeit, wert: p.anzahl }))} art={a.verlauf_art ?? "tag"} />
          </section>
          <section className="bkarte">
            <header className="bkarte__kopf">
              <h2 className="bkarte__titel">Anrufe nach Dauer</h2>
              <div className="bkarte__zahl">{dauerText(a.dauer_gesamt_s ?? 0)}</div>
            </header>
            <p className="leise bkarte__unter">Gesamtdauer · im Schnitt {dauerText(a.dauer_schnitt_s ?? null)} je Anruf</p>
            <Balkenliste zeilen={a.nach_dauer.map((k) => ({ label: k.klasse, wert: k.anzahl }))} leer="Keine Anrufe im Zeitraum." />
          </section>
          <section className="bkarte">
            <header className="bkarte__kopf">
              <h2 className="bkarte__titel">Anrufe nach Ergebnis</h2>
            </header>
            <Balkenliste zeilen={a.nach_ergebnis.map((k) => ({ label: k.ergebnis, wert: k.anzahl }))} leer="Keine Anrufe im Zeitraum." />
          </section>
          <section className="bkarte">
            <header className="bkarte__kopf">
              <h2 className="bkarte__titel">Angerufene Leads</h2>
              <div className="bkarte__zahl">{ganzText(a.eindeutige_leads)}</div>
            </header>
            <p className="leise bkarte__unter">
              Verschiedene Leads im Zeitraum{a.vergleich_eindeutige_leads !== undefined ? ` · vorher ${ganzText(a.vergleich_eindeutige_leads)}` : ""}.
            </p>
            {a.gesamt > 0 ? (
              <p className="bkarte__text">
                Im Schnitt {(a.gesamt / Math.max(1, a.eindeutige_leads)).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Anrufe je Lead.
              </p>
            ) : null}
          </section>
        </div>
      )}
    </BerichtSeite>
  );
}
